import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

import EventHero from './components/EventHero';
import WeatherForecastCard from './components/WeatherForecastCard';



import EventDescription from './components/EventDescription';
import LocationMap from './components/LocationMap';

import BookingPanel from './components/BookingPanel';

import PhotoGallery from './components/PhotoGallery';
import { eventService } from '../../services/eventService';

import ReviewsSection from './components/ReviewsSection';
import RideList from './components/rides/RideList';
import { registrationService } from '../../services/registrationService';
import { useAuth } from '../../contexts/AuthContext';

const EventDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, userProfile } = useAuth();
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [eventStatus, setEventStatus] = useState('upcoming');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [isRegistered, setIsRegistered] = useState(false);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!id) {
          throw new Error('404: Event ID is required');
        }

        // Fetch real event data from Supabase
        const event = await eventService?.getById(id);
        
        if (!event) {
          throw new Error('404: Event not found');
        }

        // Format date and time for display
        const eventDate = new Date(event?.date);
        const formattedDate = eventDate?.toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          weekday: 'long'
        });

        const formattedTime = event?.time ? 
          `${event?.time?.substring(0, 5)} - ${
            new Date(new Date(`2000-01-01T${event?.time}`).getTime() + (event?.duration || 0) * 60000)?.toTimeString()?.substring(0, 5)
          }` : 'Saat belirtilmemiş';

        setEventData({
          ...event,
          heroImage: event?.heroImage,
          heroImageAlt: event?.heroImageAlt,
          date: formattedDate,
          originalDate: event?.date, // Keep original ISO date for weather API
          rawTime: event?.time ? event?.time?.substring(0, 5) : null, // Keep raw HH:MM for weather API
          time: formattedTime,
          isNew: false,
          descriptionData: {
            description: event?.description || '',
            difficulty: event?.difficulty || 'Belirtilmemiş',
            equipment: event?.requiredEquipment ? 
              Object.entries(event?.requiredEquipment)?.filter(([_, required]) => required)?.map(([item]) => item)?.join(', ') || 'Belirtilmemiş' : 'Belirtilmemiş',
            meetingPoint: event?.location || '',
            duration: event?.duration ? `${event?.duration} dakika` : 'Belirtilmemiş'
          },
          locationData: {
            name: event?.location || 'Konum Belirtilmemiş',
            address: event?.location || '',
            coordinates: {
              lat: event?.latitude || 0,
              lng: event?.longitude || 0
            }
          },
          pricingData: {
            basePrice: event?.priceOwnBoard || 0,
            ownBoard: event?.priceOwnBoard || 0,
            rental: event?.priceWithRental || 0,
            rentalPrice: (event?.priceWithRental || 0) - (event?.priceOwnBoard || 0),
            transportPrice: 0
          }
        });

        // Determine event status
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        eventDate?.setHours(0, 0, 0, 0);
        
        if (eventDate < today) {
          setEventStatus('completed');
        } else {
          setEventStatus('upcoming');
        }

        // Save only actual event data to offline storage
        try {
          const { default: offlineStorage } = await import('../../utils/offlineStorage');
          await offlineStorage?.saveEvent({
            id: event?.id,
            ...event
          });
        } catch (storageError) {
          console.warn('Failed to save to offline storage:', storageError);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(err?.message || 'Failed to load event details');
        setIsLoading(false);
      }
    };

    window.scrollTo(0, 0);
    fetchEventData();
  }, [id]);

  // Check registration status
  useEffect(() => {
    const checkRegistration = async () => {
      if (!user || !id) {
        setIsRegistered(false);
        return;
      }

      try {
        const registered = await registrationService?.checkUserRegistration(id);
        setIsRegistered(registered);
      } catch (err) {
        console.error('Error checking registration:', err);
        setIsRegistered(false);
      }
    };

    checkRegistration();
  }, [id, user]);

  const handleBooking = (bookingData) => {
    console.log('Booking data:', bookingData);
    setShowBookingSuccess(true);
    setTimeout(() => setShowBookingSuccess(false), 3000);
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Etkinlik yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="bg-red-100 p-4 rounded-full inline-flex mb-4">
              <Icon name={error?.includes('404') ? 'FileQuestion' : 'AlertTriangle'} size={48} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {error?.includes('404') ? 'Etkinlik Bulunamadı' : 'Bir Hata Oluştu'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {error?.includes('404') 
                ? 'Aradığınız etkinlik bulunamadı veya kaldırılmış olabilir.' :'Etkinlik bilgileri yüklenirken bir sorun oluştu.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/event-discovery')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Icon name="Calendar" size={18} />
                Tüm Etkinlikler
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-medium py-2 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Icon name="Home" size={18} />
                Ana Sayfa
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Data Validation
  if (!eventData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Veri Yüklenemedi</h2>
            <p className="text-muted-foreground mb-6">
              Etkinlik bilgileri yüklenirken bir sorun oluştu. Lütfen daha sonra tekrar deneyin.
            </p>
            <button
              onClick={() => window.location?.reload()}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-6 rounded-lg flex items-center justify-center gap-2 mx-auto transition-colors"
            >
              <Icon name="RefreshCw" size={18} />
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-[60px]">
      <Header />
      {showBookingSuccess && (
        <div className="fixed top-20 right-4 bg-success text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slide-in-right">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} />
            <span>Rezervasyonunuz başarıyla oluşturuldu!</span>
          </div>
        </div>
      )}
      <EventHero {...eventData} />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            {eventData?.photos && eventData?.photos?.length > 0 && (
              <PhotoGallery photos={eventData?.photos} />
            )}
            
            {/* Tab Navigation */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('about')}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'about' ?'bg-primary text-primary-foreground border-b-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Info" size={18} />
                    <span>Hakkında</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('transport')}
                  className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === 'transport' ?'bg-primary text-primary-foreground border-b-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon name="Car" size={18} />
                    <span>Ulaşım & Paylaşım</span>
                  </div>
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'about' && (
                  <EventDescription 
                    description={eventData?.description || ''}
                    capacity={eventData?.maxParticipants}
                    currentBookings={eventData?.attendeeCount}
                    difficulty={eventData?.difficulty}
                    duration={eventData?.duration}
                    registrationDeadline={eventData?.registrationDeadline}
                  />
                )}
                
                {activeTab === 'transport' && (
                  <RideList 
                    eventId={eventData?.id} 
                    isRegistered={isRegistered}
                    isAdmin={userProfile?.role === 'admin'}
                  />
                )}
              </div>
            </div>
            
            <ReviewsSection eventId={eventData?.id} eventStatus={eventStatus} />
            
            <LocationMap {...eventData?.locationData} />
          </div>

          <div className="lg:col-span-1">
            <div className="space-y-6">
              <BookingPanel 
                pricing={eventData?.pricingData} 
                onBook={handleBooking}
                eventId={eventData?.id}
                eventStatus={eventStatus}
                capacity={eventData?.maxParticipants || 0}
                currentBookings={eventData?.attendeeCount || 0}
                eventOptions={{
                  allowOwnBoard: eventData?.allowOwnBoard,
                  allowRentalBoard: eventData?.allowRentalBoard,
                  allowStandardTicket: eventData?.allowStandardTicket,
                  priceStandardTicket: eventData?.priceStandardTicket,
                  descOwnBoard: eventData?.descOwnBoard,
                  descRentalBoard: eventData?.descRentalBoard,
                  descStandardTicket: eventData?.descStandardTicket
                }}
              />
              
              {/* Weather Forecast Card */}
              {eventData?.originalDate && eventData?.locationData?.coordinates && (
                <WeatherForecastCard 
                  eventDate={eventData?.originalDate}
                  eventTime={eventData?.rawTime}
                  latitude={eventData?.locationData?.coordinates?.lat}
                  longitude={eventData?.locationData?.coordinates?.lng}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;